import { P as PubSub } from './pubsub-CoPYzvVm.js';
import { N as Notyf } from './notyf-DIw0RvBj.js';

class QuantityInput extends HTMLElement {
  /** @type {HTMLInputElement} */
  #input = this.querySelector('input');
  /** @type {NodeListOf<HTMLButtonElement>} */
  #buttons = this.querySelectorAll('button');
  /** @type {string} */
  #context = this.dataset.context;

  connectedCallback() {
    this.#buttons.forEach((button) => {
      button.addEventListener('click', (event) => this.#handleStep(event.currentTarget.name));
    });

    this.#input.addEventListener('change', () => {
      this.#constrainAndUpdateInput(this.#input.value);
      this.#publishQuantityChange();
    });
  }

  /**
   * Handles a step button click (plus or minus).
   * Updates the input value, constrains it, and publishes a quantity change if the value changed.
   * @param {string} type - Either `'minus'` or `'plus'`
   */
  #handleStep(type) {
    const value = parseInt(this.#input.value);
    const step = parseInt(this.#input.step) || 1;
    const newValue = type === 'minus' ? Math.max(value - step, 0) : value + step;
    const updatedValue = this.#constrainAndUpdateInput(newValue);

    if (parseInt(updatedValue) !== parseInt(value)) {
      this.#publishQuantityChange();
    }
  }

  /**
   * Applies min/max constraints to the value, updates the input, and refreshes button states.
   * Shows a Notyf error when the value exceeds max.
   * @param {number|string} newValue - The new value to set
   * @returns {string} The constrained value as stored in the input
   */
  #constrainAndUpdateInput(newValue) {
    const { min, max } = this.#input;
    this.#input.value = parseInt(newValue);

    if (parseInt(newValue) >= parseInt(max) && max) {
      Notyf.error(window.toast_translations.product.quantity.error_max.replace('{{ max }}', max));
      this.#input.value = parseInt(max);
    } else if (parseInt(newValue) <= parseInt(min)) {
      this.#input.value = parseInt(min);
    }

    this.#updateButtonStates();
    return this.#input.value;
  }

  /**
   * Publishes a `quantity:change` event via PubSub with the current line index and quantity.
   */
  #publishQuantityChange() {
    PubSub.publish('quantity:change', {
      line: this.dataset?.itemIndex,
      quantity: this.#input.value,
    });
  }

  /**
   * Enables or disables plus/minus buttons based on current value and min/max.
   * In cart context, toggles trash vs minus icon when at minimum.
   */
  #updateButtonStates() {
    const { min, max, value } = this.#input;
    const minusButton = [...this.#buttons]?.find((button) => button.name === 'minus');
    const plusButton = [...this.#buttons]?.find((button) => button.name === 'plus');

    if (plusButton && max) {
      // Disable the plus button if the quantity reaches the maximum
      plusButton.disabled = parseInt(value) >= parseInt(max);
    }
    if (minusButton && min) {
      // Disable the minus button if the quantity reaches the minimum in the product context
      // Show the trash icon if the quantity reaches the minimum in the cart context
      this.#context === 'product'
        ? (minusButton.disabled = parseInt(value) <= parseInt(min))
        : this.#updateIconStates(minusButton, parseInt(value) === parseInt(min));
    }
  }

  /**
   * Toggles visibility of trash and minus icons on the button (cart context).
   * @param {HTMLButtonElement} button - The step button element
   * @param {boolean} removeAll - When true, show trash icon and hide minus; when false, the opposite
   */
  #updateIconStates(button, removeAll) {
    const trashIcon = button?.querySelector('[data-icon="regular-trash"]');
    const minusIcon = button?.querySelector('[data-icon="regular-minus"]');
    trashIcon?.classList.toggle('hidden', !removeAll);
    minusIcon?.classList.toggle('hidden', removeAll);
  }
}

customElements.define('quantity-input', QuantityInput);
